import apiClient from "./api.client.service";

export interface SkillEvidence {
  skillName: string;
  requirementId: number;
  category: "must_have" | "nice_to_have";
  // Single source-of-truth — set by pre-assessment (resume), overwritten by post-assessment (if assessed)
  status: "matches" | "partial_match" | "not_a_match";
  evidence: string;
  confidence: "low" | "medium" | "high";
  priority?: number;
}

export interface SignalSummary {
  topConcerns: SkillEvidence[];
  topStrengths: SkillEvidence[];
  topNextSteps: NextStepItem[];
}

export interface ValidationGap {
  skill: string;
  requirementId: number;
  reason: string;
}

export interface RiskIndicator {
  type: "Skill Gap" | "Experience Gap" | "Resume Signal" | "Role Mismatch";
  description: string;
  severity: "low" | "medium" | "high";
}

export interface AssessmentRound {
  roundName: string;
  interviewRoundId: string;
  roundNumber: number;
  attemptNumber: number;
  status: "qualified" | "unqualified" | "pending";
  // Deterministic — unique requirement descriptions from questions in this session (no LLM)
  skillsTested: string[];
  qualificationPercentage: number | null;  // % of evaluated questions with status='qualified'
  avgEvaluationScore: number | null;       // avg(evaluation_score) across evaluated questions; 0-100 scale
  totalQuestions: number;
  evaluatedQuestions: number;
}

export interface SkillVerdict {
  requirementId: number;
  skill: string;
  verdict: "highly_satisfactory" | "satisfactory" | "unsatisfactory";
  // All computed deterministically from raw scores — no LLM
  avgScore: number;
  questionCount: number;
  evidenceLines: string[]; // actual evaluation_reason strings from stored questions
}

export interface StrengthWeakness {
  skill: string;
  avgScore: number;
  evidenceLines: string[]; // [se.evidence] — single LLM-synthesized sentence
  status?: "matches" | "partial_match" | "not_a_match"; // for Gap/Partial tag in UI
}

export interface NextStepItem {
  skill: string;
  reason: string;
  group: "verify_before_shortlisting" | "probe_in_interview";
  severity: "high" | "medium" | "low";
}

export interface ApplicationInsightData {
  _id: string;
  jobApplicationId: string;
  orgId: string;
  jobPostId: string;
  status: "pending" | "partial" | "completed" | "failed";
  phase: "pre_assessment" | "post_assessment" | null;
  errorMessage: string | null;
  fitCategory: "Matches" | "Partial Match" | "Not a Match" | null;
  recommendation: "Proceed" | "Proceed with Caution" | "Not Recommended" | null;
  confidenceLevel: "low" | "medium" | "high" | null;
  experienceYears: number | null;
  resumeExperienceSummary: string | null;
  assessedExperienceSummary: string | null;
  basisOfInsight: string[];
  executiveSummary: string | null;
  skillEvidences: SkillEvidence[];
  signalSummary: SignalSummary | null;
  validationGaps: ValidationGap[];
  riskIndicators: RiskIndicator[];
  assessmentRounds: AssessmentRound[] | null;
  skillVerdicts: SkillVerdict[] | null;
  strengths: StrengthWeakness[] | null;
  weaknesses: StrengthWeakness[] | null;
  overallVerdict: "Highly Satisfactory" | "Satisfactory" | "Unsatisfactory" | null;
  overallReasoning: string | null;
  nextSteps: NextStepItem[];
  generatedAt: string | null;
  postAssessmentGeneratedAt: string | null;
  createdAt: string;
  updatedAt: string;

}

export interface InsightStatusResponse {
  status: string;
  phase: string | null;
}

class ApplicationInsightService {
  private readonly baseUrl: string;

  constructor() {
    this.baseUrl = process.env.REACT_APP_API_BASE_URL ?? "";
  }

  async getInsightByApplicationId(
    jobApplicationId: string
  ): Promise<ApplicationInsightData | null> {
    const response = await apiClient.get<ApplicationInsightData | null>(
      `${this.baseUrl}/admin/application-insight/${jobApplicationId}`
    );
    return response.data;
  }

  async getInsightStatus(
    jobApplicationId: string
  ): Promise<InsightStatusResponse | null> {
    const response = await apiClient.get<InsightStatusResponse | null>(
      `${this.baseUrl}/admin/application-insight/${jobApplicationId}/status`
    );
    return response.data;
  }

}

const applicationInsightService = new ApplicationInsightService();
export default applicationInsightService;
